import { createStore, combineReducers, applyMiddleware } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { composeWithDevTools } from 'redux-devtools-extension';
import { all } from 'redux-saga/effects';
import { PersistConfig, persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { createStateSyncMiddleware } from 'redux-state-sync';

import { KEY as AUTH, reducer as authReducer, sagas as authSagas } from 'services/auth';
import { KEY as CONFIGURATION, reducer as configReducer, sagas as configSagas } from 'services/configuration';
import { KEY as KIOSK, reducer as kioskReducer, sagas as kioskSagas } from 'services/kiosk';
import { KEY as TICKETING, reducer as ticketingReducer, sagas as ticketingSagas } from 'services/ticketing';
import { KEY as WILL_CALL, reducer as willCallReducer, sagas as willCallSagas } from 'services/willcall';
import { KEY as HARDWARE, reducer as hardwareReducer, sagas as hardwareSagas } from 'services/hardware';
import { KEY as EVENTS, reducer as eventReducer, sagas as eventSagas } from 'services/events';
import { KEY as CUSTOMER, reducer as customerReducer, sagas as customerSagas } from 'services/customer';
import { KEY as CART, reducer as cartReducer, sagas as cartSagas } from 'services/cart';
import { KEY as MAINTENANCE, reducer as maintenanceReducer } from 'services/maintenance';
import { KEY as SALES_ERROR, reducer as salesErrorReducer } from 'services/salesErrors';
import { KEY as MEDIA, reducer as mediaReducer, sagas as mediaSagas } from 'services/media';
import { KEY as COUNTRIES, reducer as countryReducer, sagas as countrySagas } from 'services/countries';
import { KEY as CITY_PASS, reducer as cityPassReducer, sagas as cityPassSagas } from 'services/cityPass';

import tokenMiddleware from './tokenMiddleware';
import salesErrorMiddleware from './salesErrorMiddleware';

const composeEnhancers = composeWithDevTools({});
const sagaMiddleware = createSagaMiddleware();
const stateSyncMiddleware = createStateSyncMiddleware({
  whitelist: [
    `${KIOSK}/RELOAD_APPLICATION`,
    `${KIOSK}/GET_KIOSK_ASYNC_SUCCESS`, // The following two to take the kiosk on / offline
    `${KIOSK}/UPDATE_KIOSK_ASYNC_SUCCESS`,
    `${CONFIGURATION}/GET_CONFIG_ASYNC_SUCCESS`, // In case there are config updates, make sure tabs are sync'ed
    `${MEDIA}/GET_MEDIA_SUCCESS`,
    `${MEDIA}/GET_MEDIA_PENDING`
  ]
});

// Place sagas to run here
const sagas = function* combineSagas() {
  yield all([
    ...authSagas,
    ...kioskSagas,
    ...configSagas,
    ...ticketingSagas,
    ...willCallSagas,
    ...hardwareSagas,
    ...customerSagas,
    ...cartSagas,
    ...eventSagas,
    ...mediaSagas,
    ...countrySagas,
    ...cityPassSagas
  ]);
};

const persistConfig: PersistConfig = {
  key: 'kiosk',
  storage,
  whitelist: [AUTH, CONFIGURATION, KIOSK, TICKETING, CART, WILL_CALL, MEDIA, CITY_PASS, CUSTOMER, MAINTENANCE]
};

// Place reducers to run here
const reducers = combineReducers({
  [AUTH]: authReducer,
  [CONFIGURATION]: configReducer,
  [KIOSK]: kioskReducer,
  [TICKETING]: ticketingReducer,
  [WILL_CALL]: willCallReducer,
  [HARDWARE]: hardwareReducer,
  [EVENTS]: eventReducer,
  [CUSTOMER]: customerReducer,
  [CART]: cartReducer,
  [MAINTENANCE]: maintenanceReducer,
  [SALES_ERROR]: salesErrorReducer,
  [MEDIA]: mediaReducer,
  [COUNTRIES]: countryReducer,
  [CITY_PASS]: cityPassReducer
});

const persistedReducer = persistReducer(persistConfig, reducers);

export const store = createStore(
  persistedReducer,
  composeEnhancers(applyMiddleware(stateSyncMiddleware, sagaMiddleware, tokenMiddleware, salesErrorMiddleware))
);
export const persistor = persistStore(store);

sagaMiddleware.run(sagas);
